/* eslint-disable react/prop-types */
/**
 * ConformIMF - Module 3 : Suivi des recommandations
 *
 * Page à part entière (menu dédié), distincte de la page « Contrôle & Audit »
 * - cf. décision produit du 2026-08-22 : le suivi des recommandations
 * (missions internes et externes CAC/CIF/Commission bancaire/BCEAO/ANSFD/DAI)
 * mérite sa propre entrée de menu plutôt qu'un onglet parmi d'autres.
 * Cahier de développement Module 3, § 4.9, § 5.7, § 6.6.
 */
import { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import api from "../../utils/axiosConfig";
import AuthContext from "../../context/AuthContext";
import { fadeInDown, fadeInUp } from "../../utils/animations";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

const NATURE_LABELS = {
  INTERNE: "Interne",
  CAC: "Commissaire aux comptes",
  CIF: "Contrôle interne (CIF)",
  COMMISSION_BANCAIRE: "Commission bancaire",
  BCEAO: "BCEAO",
  ANSFD: "ANSFD",
  DAI: "Direction de l'audit interne",
  AUTRE: "Autre",
};

const NATURE_EXTERNE_OPTIONS = [
  "CAC",
  "CIF",
  "COMMISSION_BANCAIRE",
  "BCEAO",
  "ANSFD",
  "DAI",
  "AUTRE",
];

const ETAT_COLORS = {
  TE: { bg: "#e8f5e9", color: "#2e7d32", label: "Totalement exécuté", icon: "check_circle" },
  PE: { bg: "#fff3e0", color: "#e65100", label: "Partiellement exécuté", icon: "warning" },
  NE: { bg: "#ffebee", color: "#c62828", label: "Non exécuté", icon: "cancel" },
};
const ETAT_NON_RENSEIGNE = {
  bg: "#f5f5f5",
  color: "#757575",
  label: "Non renseigné",
  icon: "radio_button_unchecked",
};

const PRIORITE_COLORS = {
  Haute: { bg: "#ffebee", color: "#c62828" },
  Moyenne: { bg: "#fff3e0", color: "#e65100" },
  Faible: { bg: "#f5f5f5", color: "#757575" },
};

function EtatChip({ etat }) {
  const cfg = ETAT_COLORS[etat] || ETAT_NON_RENSEIGNE;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: "0.85rem !important" }}>{cfg.icon}</Icon>}
      label={cfg.label}
      size="small"
      sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "0.68rem" }}
    />
  );
}

function EcheanceInfo({ echeance, etat }) {
  if (!echeance) return <span>-</span>;
  const date = new Date(echeance);
  const label = date.toLocaleDateString("fr-FR");
  if (etat === "TE") return <span>{label}</span>;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deltaDays = Math.round((date - today) / (1000 * 60 * 60 * 24));

  let badge = null;
  if (deltaDays < 0) {
    badge = { label: "En retard", bg: "#ffebee", color: "#c62828" };
  } else if (deltaDays <= 7) {
    badge = { label: "J-7", bg: "#ffebee", color: "#c62828" };
  } else if (deltaDays <= 14) {
    badge = { label: "J-14", bg: "#fff3e0", color: "#e65100" };
  }

  return (
    <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
      <span>{label}</span>
      {badge && (
        <Chip
          label={badge.label}
          size="small"
          sx={{
            bgcolor: badge.bg,
            color: badge.color,
            fontWeight: 700,
            fontSize: "0.6rem",
            height: 18,
          }}
        />
      )}
    </MDBox>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────────────────────────────────────

export default function SuiviRecommandations() {
  const { user } = useContext(AuthContext);
  const userRole = user?.profile?.role;
  const userId = user?.profile?.id;
  const userAllRoles = useMemo(
    () => user?.profile?.all_roles || (userRole ? [userRole] : []),
    [user, userRole]
  );
  const hasAnyRole = useCallback(
    (roles) => roles.some((r) => userAllRoles.includes(r)),
    [userAllRoles]
  );
  const isAdminGlobal = userRole === "ADMIN_GLOBAL";
  const canCreateMissionExterne = hasAnyRole(["ADMIN_INSTITUTION", "GESTIONNAIRE"]);
  const canCreateRecommandation = hasAnyRole(["ADMIN_INSTITUTION", "GESTIONNAIRE"]);
  const isControleurOnly =
    hasAnyRole(["CONTROLEUR"]) &&
    !hasAnyRole(["ADMIN_INSTITUTION", "GESTIONNAIRE", "ADMIN_GLOBAL"]);

  const canEditRecommandation = useCallback(
    (reco) => {
      if (canCreateRecommandation) return true;
      if (isControleurOnly) return reco.responsable_suivi_id === userId;
      return false;
    },
    [canCreateRecommandation, isControleurOnly, userId]
  );

  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: "", onConfirm: null });

  const showSnack = useCallback((message, severity = "success") => {
    setSnack({ open: true, message, severity });
  }, []);
  const openConfirm = (message, onConfirm) => setConfirmDialog({ open: true, message, onConfirm });

  useEffect(() => {
    if (isAdminGlobal) {
      api
        .get("/api/institutions/")
        .then((r) => setInstitutions(r.data))
        .catch(() => {});
    }
  }, [isAdminGlobal]);

  const institutionParam = isAdminGlobal ? selectedInstitution || undefined : undefined;

  // ── Missions (internes + externes) ──────────────────────────────────────
  const [missions, setMissions] = useState([]);
  const [selectedMissionId, setSelectedMissionId] = useState("");

  const loadMissions = useCallback(() => {
    const params = new URLSearchParams();
    if (institutionParam) params.append("institution_id", institutionParam);
    api
      .get(`/api/controle/missions?${params}`)
      .then((r) => setMissions(r.data))
      .catch(() => showSnack("Erreur lors du chargement des missions.", "error"));
  }, [institutionParam, showSnack]);

  useEffect(() => {
    loadMissions();
  }, [loadMissions]);

  // ── Utilisateurs de l'institution (sélecteur Responsable de suivi) ──────
  const [utilisateursInstitution, setUtilisateursInstitution] = useState([]);
  useEffect(() => {
    const params = new URLSearchParams();
    if (institutionParam) params.append("institution_id", institutionParam);
    if (!isAdminGlobal || institutionParam) {
      api
        .get(`/api/controle/utilisateurs-institution?${params}`)
        .then((r) => setUtilisateursInstitution(r.data))
        .catch(() => {});
    }
  }, [institutionParam, isAdminGlobal]);

  // ── Recommandations ───────────────────────────────────────────────────
  const [recommandations, setRecommandations] = useState([]);
  const [loadingReco, setLoadingReco] = useState(false);
  const [searchParams] = useSearchParams();
  const [filterEtat, setFilterEtat] = useState(() => searchParams.get("etat") || "");
  const [searchQuery, setSearchQuery] = useState("");

  const loadRecommandations = useCallback(() => {
    setLoadingReco(true);
    const params = new URLSearchParams();
    if (selectedMissionId) params.append("mission_id", selectedMissionId);
    if (filterEtat) params.append("etat", filterEtat);
    if (institutionParam) params.append("institution_id", institutionParam);
    api
      .get(`/api/controle/recommandations-suivi?${params}`)
      .then((r) => setRecommandations(r.data))
      .catch(() => showSnack("Erreur lors du chargement des recommandations.", "error"))
      .finally(() => setLoadingReco(false));
  }, [selectedMissionId, filterEtat, institutionParam, showSnack]);

  useEffect(() => {
    loadRecommandations();
  }, [loadRecommandations]);

  // ── Synthèses ──────────────────────────────────────────────────────────
  const [syntheseMission, setSyntheseMission] = useState(null);
  const [syntheseGlobale, setSyntheseGlobale] = useState([]);

  useEffect(() => {
    if (selectedMissionId) {
      api
        .get(`/api/controle/recommandations-suivi/synthese/${selectedMissionId}`)
        .then((r) => setSyntheseMission(r.data))
        .catch(() => setSyntheseMission(null));
    } else {
      setSyntheseMission(null);
      const params = new URLSearchParams();
      if (institutionParam) params.append("institution_id", institutionParam);
      api
        .get(`/api/controle/recommandations-suivi/synthese-globale?${params}`)
        .then((r) => setSyntheseGlobale(r.data))
        .catch(() => setSyntheseGlobale([]));
    }
  }, [selectedMissionId, institutionParam, recommandations]);

  // ── Groupement par Titre / Sous-titre, avec sous-totaux (§ 6.6) ─────────
  const recommandationsFiltrees = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return recommandations;
    return recommandations.filter((r) =>
      [r.titre, r.sous_titre, r.insuffisance, r.recommandation, r.numero_ordre]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [recommandations, searchQuery]);

  // Regroupement à deux niveaux (Titre puis Sous-titre, § 6.6). Les éléments
  // sans titre sont isolés dans un groupe "Sans titre" (comme avant) ; au
  // sein d'un titre, un sous-groupe "Sans sous-titre" n'est affiché que si ce
  // titre comporte aussi au moins un sous-titre renseigné, pour ne pas
  // ajouter un niveau d'affichage superflu aux titres qui n'en ont aucun.
  const groupes = useMemo(() => {
    const map = new Map(); // titre -> Map(sous_titre -> items[])
    const sansTitre = [];
    for (const r of recommandationsFiltrees) {
      const titreKey = (r.titre || "").trim();
      const sousKey = (r.sous_titre || "").trim();
      if (!titreKey) {
        sansTitre.push(r);
        continue;
      }
      if (!map.has(titreKey)) map.set(titreKey, new Map());
      const sousMap = map.get(titreKey);
      if (!sousMap.has(sousKey)) sousMap.set(sousKey, []);
      sousMap.get(sousKey).push(r);
    }
    const entries = Array.from(map.entries())
      .map(([titre, sousMap]) => [
        titre,
        Array.from(sousMap.entries()).sort((a, b) => a[0].localeCompare(b[0])),
      ])
      .sort((a, b) => a[0].localeCompare(b[0]));
    if (sansTitre.length) entries.push(["Sans titre", [["", sansTitre]]]);
    return entries;
  }, [recommandationsFiltrees]);

  // ── Cartes repliables par recommandation ────────────────────────────────
  const [expandedRecos, setExpandedRecos] = useState(new Set());
  const toggleReco = (recoId) => {
    setExpandedRecos((prev) => {
      const next = new Set(prev);
      if (next.has(recoId)) next.delete(recoId);
      else next.add(recoId);
      return next;
    });
  };

  // ── Pagination des recommandations (même convention que DataTable) ──────
  const RECOS_PAR_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(0);
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedMissionId, filterEtat, searchQuery]);

  const flatRecos = useMemo(() => {
    const flat = [];
    groupes.forEach(([titre, sousGroupes]) =>
      sousGroupes.forEach(([sousTitre, items]) =>
        items.forEach((r) => flat.push({ titre, sousTitre, r }))
      )
    );
    return flat;
  }, [groupes]);

  const pageCount = Math.max(1, Math.ceil(flatRecos.length / RECOS_PAR_PAGE));
  const safePage = Math.min(currentPage, pageCount - 1);
  const pageStart = safePage * RECOS_PAR_PAGE;
  const pageEnd = Math.min(pageStart + RECOS_PAR_PAGE, flatRecos.length);

  const groupesPage = useMemo(() => {
    const slice = flatRecos.slice(pageStart, pageEnd);
    const map = new Map(); // titre -> { order: string[], sousMap: Map(sousTitre -> items[]) }
    const order = [];
    slice.forEach(({ titre, sousTitre, r }) => {
      if (!map.has(titre)) {
        map.set(titre, { order: [], sousMap: new Map() });
        order.push(titre);
      }
      const g = map.get(titre);
      if (!g.sousMap.has(sousTitre)) {
        g.sousMap.set(sousTitre, []);
        g.order.push(sousTitre);
      }
      g.sousMap.get(sousTitre).push(r);
    });
    return order.map((titre) => {
      const g = map.get(titre);
      return [titre, g.order.map((st) => [st, g.sousMap.get(st)])];
    });
  }, [flatRecos, pageStart, pageEnd]);

  const calcTotaux = (items) => ({
    total: items.length,
    te: items.filter((r) => r.etat === "TE").length,
    pe: items.filter((r) => r.etat === "PE").length,
    ne: items.filter((r) => r.etat === "NE").length,
  });

  // Totaux calculés sur l'ensemble du groupe/sous-groupe (pas seulement la
  // page affichée), pour qu'ils restent exacts même si la pagination coupe
  // un groupe en deux pages. parSousTitre est indexé par "titre\u0000sousTitre".
  const groupesTotaux = useMemo(() => {
    const parTitre = new Map();
    const parSousTitre = new Map();
    groupes.forEach(([titre, sousGroupes]) => {
      const tousLesItems = sousGroupes.flatMap(([, items]) => items);
      parTitre.set(titre, calcTotaux(tousLesItems));
      sousGroupes.forEach(([sousTitre, items]) => {
        parSousTitre.set(`${titre}\u0000${sousTitre}`, calcTotaux(items));
      });
    });
    return { parTitre, parSousTitre };
  }, [groupes]);

  // ── Dialog : Nouvelle mission externe ───────────────────────────────────
  const [dialogMissionExterne, setDialogMissionExterne] = useState(false);
  const [savingMissionExterne, setSavingMissionExterne] = useState(false);
  const [formMissionExterne, setFormMissionExterne] = useState({
    reference: "",
    nature: "",
    annee_intervention: new Date().getFullYear(),
    date_debut: "",
    date_fin: "",
  });

  function openCreateMissionExterne() {
    setFormMissionExterne({
      reference: "",
      nature: "",
      annee_intervention: new Date().getFullYear(),
      date_debut: "",
      date_fin: "",
    });
    setDialogMissionExterne(true);
  }

  async function saveMissionExterne() {
    if (!formMissionExterne.reference.trim() || !formMissionExterne.nature) {
      showSnack("Référence et nature sont obligatoires.", "warning");
      return;
    }
    setSavingMissionExterne(true);
    try {
      const payload = {
        ...formMissionExterne,
        date_debut: formMissionExterne.date_debut || null,
        date_fin: formMissionExterne.date_fin || null,
      };
      const r = await api.post("/api/controle/missions/externes", payload);
      showSnack("Mission externe créée avec succès.");
      setDialogMissionExterne(false);
      loadMissions();
      setSelectedMissionId(r.data.id);
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de la création.", "error");
    } finally {
      setSavingMissionExterne(false);
    }
  }

  // ── Dialog : Recommandation (création / édition) ────────────────────────
  const [dialogRecommandation, setDialogRecommandation] = useState(false);
  const [editingRecommandation, setEditingRecommandation] = useState(null);
  const [savingRecommandation, setSavingRecommandation] = useState(false);
  const emptyFormReco = {
    mission_id: "",
    titre: "",
    sous_titre: "",
    insuffisance: "",
    recommandation: "",
    plan_action: "",
    echeance: "",
    responsable_suivi_id: "",
    etat: "",
    indicateurs_verifiables: "",
    priorite: "",
    observations: "",
  };
  const [formReco, setFormReco] = useState(emptyFormReco);

  function openCreateRecommandation() {
    setEditingRecommandation(null);
    setFormReco({ ...emptyFormReco, mission_id: selectedMissionId || "" });
    setDialogRecommandation(true);
  }

  function openEditRecommandation(reco) {
    setEditingRecommandation(reco);
    setFormReco({
      mission_id: reco.mission_id,
      titre: reco.titre || "",
      sous_titre: reco.sous_titre || "",
      insuffisance: reco.insuffisance || "",
      recommandation: reco.recommandation || "",
      plan_action: reco.plan_action || "",
      echeance: reco.echeance || "",
      responsable_suivi_id: reco.responsable_suivi_id || "",
      etat: reco.etat || "",
      indicateurs_verifiables: reco.indicateurs_verifiables || "",
      priorite: reco.priorite || "",
      observations: reco.observations || "",
    });
    setDialogRecommandation(true);
  }

  async function saveRecommandation() {
    if (!editingRecommandation && !formReco.mission_id) {
      showSnack("Sélectionnez une mission.", "warning");
      return;
    }
    if (!formReco.insuffisance.trim()) {
      showSnack("L'insuffisance est obligatoire.", "warning");
      return;
    }
    setSavingRecommandation(true);
    try {
      if (editingRecommandation) {
        // Un CONTROLEUR ne peut modifier que ces 4 champs (§7.1) - le serveur
        // rejette la requête si un autre champ est présent dans le payload,
        // même avec sa valeur inchangée. On limite donc l'envoi pour lui.
        const payload = isControleurOnly
          ? {
              etat: formReco.etat || null,
              plan_action: formReco.plan_action || null,
              indicateurs_verifiables: formReco.indicateurs_verifiables || null,
              observations: formReco.observations || null,
            }
          : {
              titre: formReco.titre || null,
              sous_titre: formReco.sous_titre || null,
              insuffisance: formReco.insuffisance,
              recommandation: formReco.recommandation || null,
              plan_action: formReco.plan_action || null,
              echeance: formReco.echeance || null,
              responsable_suivi_id: formReco.responsable_suivi_id || null,
              etat: formReco.etat || null,
              indicateurs_verifiables: formReco.indicateurs_verifiables || null,
              priorite: formReco.priorite || null,
              observations: formReco.observations || null,
            };
        await api.put(`/api/controle/recommandations-suivi/${editingRecommandation.id}`, payload);
        showSnack("Recommandation mise à jour avec succès.");
      } else {
        await api.post("/api/controle/recommandations-suivi", {
          mission_id: Number(formReco.mission_id),
          titre: formReco.titre || null,
          sous_titre: formReco.sous_titre || null,
          insuffisance: formReco.insuffisance,
          recommandation: formReco.recommandation || null,
          plan_action: formReco.plan_action || null,
          echeance: formReco.echeance || null,
          responsable_suivi_id: formReco.responsable_suivi_id || null,
          priorite: formReco.priorite || null,
        });
        showSnack("Recommandation créée avec succès.");
      }
      setDialogRecommandation(false);
      loadRecommandations();
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSavingRecommandation(false);
    }
  }

  // ── Preuves ────────────────────────────────────────────────────────────
  const [uploadingPreuveId, setUploadingPreuveId] = useState(null);

  async function uploadPreuveSuivi(recommandationId, file) {
    const fd = new FormData();
    fd.append("file", file);
    setUploadingPreuveId(recommandationId);
    try {
      await api.post(`/api/controle/recommandations-suivi/${recommandationId}/preuves`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      showSnack("Preuve téléversée avec succès.");
      loadRecommandations();
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors du téléversement.", "error");
    } finally {
      setUploadingPreuveId(null);
    }
  }

  function deletePreuveSuivi(preuveId) {
    openConfirm("Supprimer cette preuve ?", async () => {
      try {
        await api.delete(`/api/controle/preuves-suivi/${preuveId}`);
        showSnack("Preuve supprimée avec succès.");
        loadRecommandations();
      } catch {
        showSnack("Erreur lors de la suppression.", "error");
      }
    });
  }

  // ── Rendu : tuiles de synthèse ───────────────────────────────────────────
  function renderSyntheseTiles(s) {
    const tiles = [
      { label: "Total", value: s.total, icon: "list_alt", color: "#0A3475" },
      {
        label: "Totalement exécuté",
        value: `${s.te} (${s.te_pct}%)`,
        icon: "check_circle",
        color: "#2e7d32",
      },
      {
        label: "Partiellement exécuté",
        value: `${s.pe} (${s.pe_pct}%)`,
        icon: "hourglass_top",
        color: "#e65100",
      },
      { label: "Non exécuté", value: `${s.ne} (${s.ne_pct}%)`, icon: "cancel", color: "#c62828" },
    ];
    return (
      <Grid container spacing={2} mb={3}>
        {tiles.map((t) => (
          <Grid item xs={6} md={3} key={t.label}>
            <Card sx={{ p: 2, textAlign: "center", height: "100%" }}>
              <Icon sx={{ color: t.color, fontSize: "1.6rem !important" }}>{t.icon}</Icon>
              <MDTypography variant="h5" sx={{ color: t.color, fontWeight: 700, mt: 0.5 }}>
                {t.value}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {t.label}
              </MDTypography>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  function renderSyntheseGlobale() {
    if (syntheseGlobale.length === 0) return null;
    return (
      <MDBox mb={3}>
        <MDTypography variant="h6" sx={{ color: "#0A3475", mb: 1 }}>
          Synthèse globale de fin de période
        </MDTypography>
        <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
          Nature × Année, sur les 5 dernières interventions actives de chaque nature.
        </MDTypography>
        <Card sx={{ boxShadow: "none", border: "1px solid #e0e0e0", p: 2 }}>
          {syntheseGlobale.map((l, i) => (
            <MDBox
              key={`${l.nature}-${l.annee}`}
              py={1.3}
              sx={{
                borderBottom: i < syntheseGlobale.length - 1 ? "1px solid #f0f0f0" : "none",
              }}
            >
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                mb={0.6}
              >
                <MDTypography variant="button" fontWeight="medium">
                  {NATURE_LABELS[l.nature] || l.nature}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
                  {l.annee} · {l.total} intervention{l.total > 1 ? "s" : ""}
                </MDTypography>
              </MDBox>
              {l.total === 0 ? (
                <MDBox
                  sx={{
                    height: 14,
                    borderRadius: "7px",
                    bgcolor: "#f5f5f5",
                    border: "1px dashed #ddd",
                  }}
                />
              ) : (
                <>
                  <MDBox
                    display="flex"
                    width="100%"
                    height={14}
                    borderRadius="7px"
                    overflow="hidden"
                    sx={{ bgcolor: "#f5f5f5" }}
                  >
                    {l.te_pct > 0 && (
                      <Tooltip title={`Totalement exécuté : ${l.te_pct}%`}>
                        <MDBox sx={{ width: `${l.te_pct}%`, bgcolor: "#2e7d32" }} />
                      </Tooltip>
                    )}
                    {l.pe_pct > 0 && (
                      <Tooltip title={`Partiellement exécuté : ${l.pe_pct}%`}>
                        <MDBox sx={{ width: `${l.pe_pct}%`, bgcolor: "#e65100" }} />
                      </Tooltip>
                    )}
                    {l.ne_pct > 0 && (
                      <Tooltip title={`Non exécuté : ${l.ne_pct}%`}>
                        <MDBox sx={{ width: `${l.ne_pct}%`, bgcolor: "#c62828" }} />
                      </Tooltip>
                    )}
                  </MDBox>
                  <MDBox display="flex" gap={2} mt={0.5}>
                    <MDTypography variant="caption" sx={{ color: "#2e7d32", fontWeight: 600 }}>
                      TE {l.te_pct}%
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#e65100", fontWeight: 600 }}>
                      PE {l.pe_pct}%
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#c62828", fontWeight: 600 }}>
                      NE {l.ne_pct}%
                    </MDTypography>
                  </MDBox>
                </>
              )}
            </MDBox>
          ))}
        </Card>
      </MDBox>
    );
  }

  // ── Rendu : tableau groupé (§ 6.6) ───────────────────────────────────────
  function renderTableauSuivi() {
    if (loadingReco) {
      return (
        <MDBox display="flex" justifyContent="center" p={5}>
          <CircularProgress color="info" />
        </MDBox>
      );
    }
    if (recommandationsFiltrees.length === 0) {
      return (
        <Card>
          <MDBox p={4} textAlign="center">
            <Icon sx={{ fontSize: 48, color: "grey.400" }}>track_changes</Icon>
            <MDTypography color="text" mt={1} variant="h6">
              Aucune recommandation pour ce filtre.
            </MDTypography>
          </MDBox>
        </Card>
      );
    }

    return (
      <MDBox>
        {groupesPage.map(([titre, sousGroupes]) => {
          const totaux = groupesTotaux.parTitre.get(titre) || {
            total: 0,
            te: 0,
            pe: 0,
            ne: 0,
          };
          // N'affiche un sous-niveau que si ce titre comporte au moins un
          // sous-titre renseigné ; sinon, les recommandations s'affichent
          // directement sous le titre, comme avant l'ajout du sous-titre.
          const afficherSousTitres =
            sousGroupes.length > 1 || (sousGroupes.length === 1 && sousGroupes[0][0] !== "");
          return (
            <MDBox key={titre} mb={3}>
              <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={1}>
                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#0A3475" }}>
                  {titre}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
                  {totaux.total} recommandation(s) · TE {totaux.te} · PE {totaux.pe} · NE{" "}
                  {totaux.ne}
                </MDTypography>
              </MDBox>
              {sousGroupes.map(([sousTitre, items]) => {
                const sousTotaux = groupesTotaux.parSousTitre.get(`${titre}\u0000${sousTitre}`) || {
                  total: items.length,
                  te: 0,
                  pe: 0,
                  ne: 0,
                };
                return (
                  <MDBox key={sousTitre} ml={afficherSousTitres ? 2 : 0} mb={1.5}>
                    {afficherSousTitres && (
                      <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={0.5}>
                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#186A81" }}>
                          {sousTitre || "Sans sous-titre"}
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
                          {sousTotaux.total} recommandation(s) · TE {sousTotaux.te} · PE{" "}
                          {sousTotaux.pe} · NE {sousTotaux.ne}
                        </MDTypography>
                      </MDBox>
                    )}
                    {items.map((r) => (
                      <Accordion
                        key={r.id}
                        expanded={expandedRecos.has(r.id)}
                        onChange={() => toggleReco(r.id)}
                        sx={{
                          mb: 1,
                          "&:before": { display: "none" },
                          borderRadius: "8px !important",
                          overflow: "hidden",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<Icon>expand_more</Icon>}
                          sx={{ background: "#f8f9fa" }}
                        >
                          <MDBox
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            width="100%"
                            flexWrap="wrap"
                            pr={1}
                          >
                            <Chip
                              label={r.numero_ordre}
                              size="small"
                              sx={{ fontWeight: 700, bgcolor: "#0A3475", color: "#fff" }}
                            />
                            <MDTypography
                              variant="button"
                              sx={{ flexGrow: 1, minWidth: 160, fontSize: "0.8rem" }}
                            >
                              {r.insuffisance}
                              {!selectedMissionId && (
                                <MDTypography
                                  variant="caption"
                                  sx={{ display: "block", color: "#9e9e9e" }}
                                >
                                  {r.mission_reference}
                                </MDTypography>
                              )}
                            </MDTypography>
                            <EtatChip etat={r.etat} />
                            <EcheanceInfo echeance={r.echeance} etat={r.etat} />
                            {r.priorite && (
                              <Chip
                                label={r.priorite}
                                size="small"
                                sx={{
                                  bgcolor: PRIORITE_COLORS[r.priorite]?.bg,
                                  color: PRIORITE_COLORS[r.priorite]?.color,
                                  fontWeight: 600,
                                }}
                              />
                            )}
                            <MDTypography variant="caption" sx={{ color: "#5c6b7f" }}>
                              {r.responsable_suivi_nom || "-"}
                            </MDTypography>
                            {canEditRecommandation(r) && (
                              <Tooltip title="Modifier">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditRecommandation(r);
                                  }}
                                >
                                  <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                                </IconButton>
                              </Tooltip>
                            )}
                          </MDBox>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                sx={{ color: "#5c6b7f" }}
                              >
                                Recommandation
                              </MDTypography>
                              <MDTypography variant="body2" sx={{ fontSize: "0.8rem" }}>
                                {r.recommandation || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                sx={{ color: "#5c6b7f" }}
                              >
                                Plan d&apos;actions
                              </MDTypography>
                              <MDTypography variant="body2" sx={{ fontSize: "0.8rem" }}>
                                {r.plan_action || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                sx={{ color: "#5c6b7f" }}
                              >
                                Indicateurs vérifiables
                              </MDTypography>
                              <MDTypography variant="body2" sx={{ fontSize: "0.8rem" }}>
                                {r.indicateurs_verifiables || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                sx={{ color: "#5c6b7f" }}
                              >
                                Observations
                              </MDTypography>
                              <MDTypography variant="body2" sx={{ fontSize: "0.8rem" }}>
                                {r.observations || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                sx={{ color: "#5c6b7f", display: "block", mb: 0.5 }}
                              >
                                Preuve(s)
                              </MDTypography>
                              <MDBox display="flex" flexWrap="wrap" gap={0.6}>
                                {r.preuves.map((p) => (
                                  <Chip
                                    key={p.id}
                                    icon={
                                      <Icon sx={{ fontSize: "0.9rem !important" }}>
                                        insert_drive_file
                                      </Icon>
                                    }
                                    label={p.nom_fichier}
                                    size="small"
                                    variant="outlined"
                                    sx={{ maxWidth: 200 }}
                                    onClick={() =>
                                      window.open(
                                        `${api.defaults.baseURL}/api/controle/preuves-suivi/${p.id}/fichier`,
                                        "_blank"
                                      )
                                    }
                                    onDelete={
                                      canEditRecommandation(r)
                                        ? () => deletePreuveSuivi(p.id)
                                        : undefined
                                    }
                                  />
                                ))}
                                {canEditRecommandation(r) && (
                                  <MDButton
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    component="label"
                                    disabled={uploadingPreuveId === r.id}
                                    sx={{ minWidth: 0 }}
                                  >
                                    {uploadingPreuveId === r.id ? (
                                      <CircularProgress size={14} color="inherit" />
                                    ) : (
                                      <Icon sx={{ fontSize: "1rem !important" }}>upload_file</Icon>
                                    )}
                                    <input
                                      type="file"
                                      hidden
                                      accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.csv"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) uploadPreuveSuivi(r.id, file);
                                        e.target.value = "";
                                      }}
                                    />
                                  </MDButton>
                                )}
                              </MDBox>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </MDBox>
                );
              })}
            </MDBox>
          );
        })}

        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={3}
        >
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Affichage de {flatRecos.length === 0 ? 0 : pageStart + 1} à {pageEnd} sur{" "}
              {flatRecos.length} entrées
            </MDTypography>
          </MDBox>
          {pageCount > 1 && (
            <MDPagination variant="gradient" color="info">
              {safePage > 0 && (
                <MDPagination item onClick={() => setCurrentPage(safePage - 1)}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </MDPagination>
              )}
              {Array.from({ length: pageCount }, (_, i) => i).map((i) => (
                <MDPagination
                  key={i}
                  item
                  active={i === safePage}
                  onClick={() => setCurrentPage(i)}
                >
                  {i + 1}
                </MDPagination>
              ))}
              {safePage < pageCount - 1 && (
                <MDPagination item onClick={() => setCurrentPage(safePage + 1)}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </MDPagination>
              )}
            </MDPagination>
          )}
        </MDBox>
      </MDBox>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="purple"
            borderRadius="lg"
            coloredShadow="dark"
            sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
          >
            <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
              Suivi des recommandations
            </MDTypography>
            <MDTypography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.72)", display: "block", mt: 0.3 }}
            >
              Missions internes et externes - CAC, CIF, Commission bancaire, BCEAO, ANSFD, DAI
            </MDTypography>
          </MDBox>

          <MDBox px={2} py={3}>
            {isAdminGlobal && (
              <MDBox mb={2} display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
                <FormControl sx={{ minWidth: 230 }}>
                  <Select
                    displayEmpty
                    value={selectedInstitution || ""}
                    onChange={(e) => setSelectedInstitution(e.target.value)}
                    sx={{ height: "45px" }}
                    renderValue={(v) =>
                      v ? (
                        institutions.find((i) => i.id === Number(v))?.nom
                      ) : (
                        <span style={{ color: "#aaa", fontSize: "0.875rem" }}>
                          Sélectionner une institution
                        </span>
                      )
                    }
                  >
                    {institutions.map((inst) => (
                      <MenuItem key={inst.id} value={inst.id}>
                        {inst.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MDBox>
            )}

            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              mb={3}
            >
              <MDInput
                placeholder="Rechercher une recommandation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 260 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl sx={{ minWidth: 260 }}>
                <InputLabel>Mission</InputLabel>
                <Select
                  label="Mission"
                  value={selectedMissionId}
                  onChange={(e) => setSelectedMissionId(e.target.value)}
                  sx={{ height: "45px" }}
                >
                  <MenuItem value="">Toutes les missions</MenuItem>
                  {missions.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.reference} - {NATURE_LABELS[m.nature] || m.nature} {m.annee_intervention}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <MDBox display="flex" gap={1} flexWrap="wrap">
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>État</InputLabel>
                  <Select
                    label="État"
                    value={filterEtat}
                    onChange={(e) => setFilterEtat(e.target.value)}
                    sx={{ height: "45px" }}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="TE">Totalement exécuté</MenuItem>
                    <MenuItem value="PE">Partiellement exécuté</MenuItem>
                    <MenuItem value="NE">Non exécuté</MenuItem>
                    <MenuItem value="NON_TE">Non exécutées (PE + NE)</MenuItem>
                  </Select>
                </FormControl>
                {canCreateMissionExterne && (
                  <MDButton variant="outlined" color="info" onClick={openCreateMissionExterne}>
                    <Icon sx={{ mr: 0.5 }}>add</Icon> Mission externe
                  </MDButton>
                )}
                {canCreateRecommandation && (
                  <MDButton variant="gradient" color="info" onClick={openCreateRecommandation}>
                    <Icon sx={{ mr: 0.5 }}>add</Icon> Recommandation
                  </MDButton>
                )}
              </MDBox>
            </MDBox>

            {selectedMissionId && syntheseMission && renderSyntheseTiles(syntheseMission)}
            {!selectedMissionId && renderSyntheseGlobale()}

            {renderTableauSuivi()}
          </MDBox>
        </Card>
      </MDBox>

      {/* Dialog : Nouvelle mission externe */}
      <Dialog
        open={dialogMissionExterne}
        onClose={() => setDialogMissionExterne(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            Nouvelle mission externe
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              fullWidth
              size="small"
              label="Référence"
              value={formMissionExterne.reference}
              onChange={(e) =>
                setFormMissionExterne({ ...formMissionExterne, reference: e.target.value })
              }
              placeholder="ex. RAPPORT-BCEAO-2026-03"
              inputProps={{ maxLength: 30 }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Nature</InputLabel>
              <Select
                label="Nature"
                value={formMissionExterne.nature}
                onChange={(e) =>
                  setFormMissionExterne({ ...formMissionExterne, nature: e.target.value })
                }
              >
                {NATURE_EXTERNE_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {NATURE_LABELS[n]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Année d'intervention"
              value={formMissionExterne.annee_intervention}
              onChange={(e) =>
                setFormMissionExterne({
                  ...formMissionExterne,
                  annee_intervention: Number(e.target.value),
                })
              }
            />
            <MDBox display="flex" gap={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date de début"
                InputLabelProps={{ shrink: true }}
                value={formMissionExterne.date_debut}
                onChange={(e) =>
                  setFormMissionExterne({ ...formMissionExterne, date_debut: e.target.value })
                }
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date de fin"
                InputLabelProps={{ shrink: true }}
                value={formMissionExterne.date_fin}
                onChange={(e) =>
                  setFormMissionExterne({ ...formMissionExterne, date_fin: e.target.value })
                }
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogMissionExterne(false)}
            disabled={savingMissionExterne}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={saveMissionExterne}
            disabled={savingMissionExterne}
          >
            {savingMissionExterne ? <CircularProgress size={16} color="inherit" /> : "Créer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog : Recommandation */}
      <Dialog
        open={dialogRecommandation}
        onClose={() => setDialogRecommandation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            {editingRecommandation
              ? `Recommandation ${editingRecommandation.numero_ordre}`
              : "Nouvelle recommandation"}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            {!editingRecommandation && (
              <FormControl fullWidth size="small">
                <InputLabel>Mission</InputLabel>
                <Select
                  label="Mission"
                  value={formReco.mission_id}
                  onChange={(e) => setFormReco({ ...formReco, mission_id: e.target.value })}
                >
                  {missions.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.reference} - {NATURE_LABELS[m.nature] || m.nature} {m.annee_intervention}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <MDBox display="flex" gap={2}>
              <TextField
                fullWidth
                size="small"
                label="Titre (regroupement)"
                value={formReco.titre}
                onChange={(e) => setFormReco({ ...formReco, titre: e.target.value })}
                placeholder="ex. KYC, Gouvernance…"
                inputProps={{ maxLength: 300 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Sous-titre (regroupement)"
                value={formReco.sous_titre}
                onChange={(e) => setFormReco({ ...formReco, sous_titre: e.target.value })}
                placeholder="ex. Identification, Vigilance…"
                inputProps={{ maxLength: 300 }}
              />
            </MDBox>
            <TextField
              fullWidth
              size="small"
              label="Insuffisance"
              multiline
              rows={2}
              value={formReco.insuffisance}
              onChange={(e) => setFormReco({ ...formReco, insuffisance: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              label="Recommandation"
              multiline
              rows={2}
              value={formReco.recommandation}
              onChange={(e) => setFormReco({ ...formReco, recommandation: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              label="Plan d'actions"
              multiline
              rows={2}
              value={formReco.plan_action}
              onChange={(e) => setFormReco({ ...formReco, plan_action: e.target.value })}
            />
            <MDBox display="flex" gap={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Échéance"
                InputLabelProps={{ shrink: true }}
                value={formReco.echeance}
                onChange={(e) => setFormReco({ ...formReco, echeance: e.target.value })}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Priorité</InputLabel>
                <Select
                  label="Priorité"
                  value={formReco.priorite}
                  onChange={(e) => setFormReco({ ...formReco, priorite: e.target.value })}
                >
                  <MenuItem value="">-</MenuItem>
                  <MenuItem value="Haute">Haute</MenuItem>
                  <MenuItem value="Moyenne">Moyenne</MenuItem>
                  <MenuItem value="Faible">Faible</MenuItem>
                </Select>
              </FormControl>
            </MDBox>
            <FormControl fullWidth size="small" disabled={isControleurOnly}>
              <InputLabel>Responsable de suivi</InputLabel>
              <Select
                label="Responsable de suivi"
                value={formReco.responsable_suivi_id}
                onChange={(e) => setFormReco({ ...formReco, responsable_suivi_id: e.target.value })}
              >
                <MenuItem value="">-</MenuItem>
                {utilisateursInstitution.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {editingRecommandation && (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>État de mise en œuvre</InputLabel>
                  <Select
                    label="État de mise en œuvre"
                    value={formReco.etat}
                    onChange={(e) => setFormReco({ ...formReco, etat: e.target.value })}
                  >
                    <MenuItem value="">-</MenuItem>
                    <MenuItem value="TE">Totalement exécuté</MenuItem>
                    <MenuItem value="PE">Partiellement exécuté</MenuItem>
                    <MenuItem value="NE">Non exécuté</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  label="Indicateurs vérifiables"
                  multiline
                  rows={2}
                  value={formReco.indicateurs_verifiables}
                  onChange={(e) =>
                    setFormReco({ ...formReco, indicateurs_verifiables: e.target.value })
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Observations"
                  multiline
                  rows={2}
                  value={formReco.observations}
                  onChange={(e) => setFormReco({ ...formReco, observations: e.target.value })}
                />
              </>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogRecommandation(false)}
            disabled={savingRecommandation}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={saveRecommandation}
            disabled={savingRecommandation}
          >
            {savingRecommandation ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{confirmDialog.message}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="secondary"
          >
            Annuler
          </MDButton>
          <MDButton
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm?.();
            }}
            variant="gradient"
            color="warning"
          >
            Confirmer
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}
